import { useEffect, useState } from "react";
import { buildXContextHeader } from "@utils/api/headers";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { openLocationSubscription } from "./appsyncRealtime";
import { AVAILABILITY_CHECK_MS, reconnectDelayMs, refreshDelayMs, toDriverPosition } from "./trackingSession";
import { fetchTrackingSession } from "./trackingSessionApi";
import type { TrackingSession } from "./trackingSession";
import type { DeliveryTracking } from "./types";

/**
 * 配送員即時位置。
 *
 * 1. 向 app-api 要一張 10 分鐘的通行證（同時拿到配送員、連線位址、起始位置）。
 * 2. 帶通行證直接訂閱 AppSync；配送員每回報一次就推過來（約 1～2 秒），不經過 app-api。
 * 3. 到期前 2 分鐘換新通行證並重連；換不到（available: false）代表訂單已結束，整塊收掉。
 *
 * 規格：lpg-iot-core-lambda docs/customer-realtime-tracking-spec.md 第 8 節。
 * 對外介面（tracking / loading / unavailable）與輪詢版相同，畫面層不用動。
 */
export function useDriverLocation({
  orderId,
  enabled,
  xContext,
}: {
  orderId?: string;
  /** 僅在「配送中」時開啟 */
  enabled: boolean;
  xContext?: string | null;
}) {
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const [tracking, setTracking] = useState<DeliveryTracking | null>(null);
  const [loading, setLoading] = useState(false);
  /**
   * 這張單沒有追蹤可看（不在配送中、第一次就要不到通行證）。
   * 與「暫時還沒有座標」不同：這時整張卡收掉，讓頁面退回原本的狀態文案。
   */
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled || !orderId || !cusJwtToken) {
      setTracking(null);
      setUnavailable(false);
      return;
    }
    // 換單時不沿用上一張單的座標
    setTracking(null);
    setUnavailable(false);

    // "X-Context" 拿不到值時整個 key 都不送（buildXContextHeader 的「無 context」語意），
    // 不能塞空字串——fetchTrackingSession 的 headers 型別是 Record<string, string>，
    // 而 xContext ?? buildXContextHeader(...) 可能是 undefined。
    const xContextValue = xContext ?? buildXContextHeader(chooseIdentity);
    const headers: Record<string, string> = {
      Authorization: `Bearer ${cusJwtToken}`,
      ...(xContextValue ? { "X-Context": xContextValue } : {}),
    };

    let canceled = false;
    let sub: { close(): void } | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    /** 這輪是否曾拿到通行證：用來區分「暫時失敗」與「本來就沒有」 */
    let hadSession = false;
    /**
     * 訂單已結束（通行證回過 available:false）：終局狀態，這個 effect 生命週期內
     * 不再重連——不像「暫時失敗」還有機會在下次 visible 時復原。
     */
    let ended = false;

    /** 定期確認訂單還在不在配送中（見 AVAILABILITY_CHECK_MS） */
    let checkTimer: ReturnType<typeof setInterval> | null = null;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const clearCheck = () => {
      if (checkTimer) clearInterval(checkTimer);
      checkTimer = null;
    };
    const stop = () => {
      clearTimer();
      clearCheck();
      sub?.close();
      sub = null;
    };
    const giveUp = () => {
      stop();
      setUnavailable(true);
      setLoading(false);
    };

    const scheduleReconnect = () => {
      if (canceled || ended || document.hidden) return;
      clearTimer();
      timer = setTimeout(() => {
        // 分頁可能在計時器等待期間切到背景；背景時交給 visibilitychange 處理，這裡不重連
        if (canceled || ended || document.hidden) return;
        attempt += 1;
        void start();
      }, reconnectDelayMs(attempt));
    };

    const start = async () => {
      if (canceled || ended) return;
      let result: TrackingSession;
      try {
        result = await fetchTrackingSession(orderId, headers);
      } catch (error) {
        if (canceled) return;
        // 背景重試不跳 toast（會變成連環彈窗）；從沒拿到過就判定這張單沒有追蹤可看
        console.error("取得配送追蹤通行證失敗:", error);
        if (!hadSession) giveUp();
        else scheduleReconnect();
        return;
      }
      // await 期間分頁可能已切到背景：這批資料不再算數，交給下次 visibilitychange 重新要一次
      if (canceled || ended || document.hidden) return;
      if (!result.available) {
        // 訂單已不在配送中：關掉連線，不再顯示配送員位置，且整個 effect 生命週期不再重連
        ended = true;
        giveUp();
        return;
      }
      // 收窄後存成 const：let 變數在下面的 callback 裡會失去收窄
      const session = result;

      hadSession = true;
      setLoading(false);
      // 這一輪成功拿到通行證，代表訂單還在配送中：先前若因暫時性失敗顯示過「不可用」要收回
      setUnavailable(false);
      setTracking((prev) => ({
        driver: session.driver,
        eta: session.eta,
        stopsAhead: session.stopsAhead,
        // 起始位置拿不到時沿用上一筆，避免續發時 marker 閃掉
        position: toDriverPosition(session.position) ?? prev?.position ?? null,
      }));

      sub?.close();
      sub = openLocationSubscription({
        wssUrl: session.realtime.wssUrl,
        httpHost: session.realtime.httpHost,
        token: session.token,
        courierId: session.courierId,
        // 退避歸零要等訂閱真的被接受（start_ack）；只看 connection_ack 的話，
        // 訂閱被拒時會固定以第一段間隔無限重試
        onSubscribed: () => {
          attempt = 0;
        },
        onLocation: (raw) => {
          const position = toDriverPosition(raw);
          if (!position || canceled) return;
          setTracking((prev) => (prev ? { ...prev, position } : prev));
        },
        onAuthError: () => {
          sub?.close();
          sub = null;
          scheduleReconnect();
        },
        onClose: () => {
          sub = null;
          scheduleReconnect();
        },
      });

      clearTimer();
      timer = setTimeout(() => {
        // 到期前才會觸發；分頁若已在背景，交給 visibilitychange 在回前台時處理，這裡不重連
        if (canceled || ended || document.hidden) return;
        void start();
      }, refreshDelayMs(session.expiresAt, Date.now()));

      /**
       * 每分鐘問一次「這張單還在配送中嗎」。
       *
       * 只問，不動連線：available 還是 true 的話，順手把 driver／eta／stopsAhead 更新掉
       * （這些值在配送過程中會變，而 AppSync 只推座標）。回 false 就是訂單結束了，
       * 整塊收掉——不能繼續顯示配送員的位置，他已經在服務下一位客戶了。
       */
      clearCheck();
      checkTimer = setInterval(async () => {
        if (canceled || ended || document.hidden) return;
        let latest: TrackingSession;
        try {
          latest = await fetchTrackingSession(orderId, headers);
        } catch {
          // 暫時性失敗不處理：連線還在，下一分鐘再問
          return;
        }
        if (canceled || ended) return;
        if (!latest.available) {
          ended = true;
          giveUp();
          return;
        }
        // 收窄後存成 const：let 變數在下面的 callback 裡會失去收窄（同本檔案 start() 的作法）
        const current = latest;
        setTracking((prev) =>
          prev ? { ...prev, driver: current.driver, eta: current.eta, stopsAhead: current.stopsAhead } : prev,
        );
      }, AVAILABILITY_CHECK_MS);
    };

    /**
     * 只在分頁可見時連線。LIFF 在 LINE 內建 webview，切回聊天室是常態；
     * 背景時關掉連線，回來先換新通行證（背景期間可能已過期）再連。
     */
    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      if (canceled || ended) return;
      attempt = 0;
      void start();
    };

    setLoading(true);
    void start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      canceled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [orderId, enabled, cusJwtToken, xContext, chooseIdentity]);

  return { tracking, loading, unavailable };
}
